import React, { useState, useEffect } from 'react';
import { getSubmissionLinkTemplateSettings, updateSubmissionLinkTemplateSettings } from '../services/emailService';
import '../styles/EmailTemplateSettings.css';

const SubmissionLinkEmailTemplateEditor = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    subject: '',
    title: '',
    subtitle: '',
    greeting: '',
    content: '',
    instructions: '',
    buttonText: '',
    closing: '',
    footer: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    try {
      const result = await getSubmissionLinkTemplateSettings();
      if (result.success) {
        setSettings(result.settings);
      }
    } catch (error) {
      console.error('Error fetching submission link template settings:', error);
      setMessage('Error loading email template settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const result = await updateSubmissionLinkTemplateSettings(settings);
      if (result.success) {
        setMessage('Email template settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving submission link template settings:', error);
      setMessage('Error saving email template settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      subject: 'Your Annual Submission Link - {year}',
      title: 'Annual Submission Portal',
      subtitle: 'Tennessee-Western Kentucky Conference',
      greeting: 'Dear {firstName} {lastName},',
      content: 'Your annual submission portal is now available. Please use the link below to submit your required documents and information for {year}.',
      instructions: 'Please complete your submission by the deadline. You can save your progress and return to complete it later if needed.',
      buttonText: 'Access Submission Portal',
      closing: 'If you have any questions or need assistance, please contact the Board of Ordained Ministry office.',
      footer: 'This link is unique to you and should not be shared with others. The link will remain active until the submission deadline.'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content email-template-modal">
        <div className="modal-header">
          <h2>Submission Link Email Template</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {loading ? (
          <div className="loading">Loading email template settings...</div>
        ) : (
          <div className="email-template-content">
            <div className="settings-panel">
              <div className="form-group">
                <label htmlFor="subject">Email Subject</label>
                <input
                  type="text"
                  id="subject"
                  value={settings.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="Your Annual Submission Link - {year}"
                />
                <small>Available variables: {'{firstName}'}, {'{lastName}'}, {'{year}'}</small>
              </div>

              <div className="form-group">
                <label htmlFor="title">Email Title</label>
                <input
                  type="text"
                  id="title"
                  value={settings.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Annual Submission Portal"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subtitle">Email Subtitle</label>
                <input
                  type="text"
                  id="subtitle"
                  value={settings.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  placeholder="Tennessee-Western Kentucky Conference"
                />
              </div>

              <div className="form-group">
                <label htmlFor="greeting">Greeting</label>
                <input
                  type="text"
                  id="greeting"
                  value={settings.greeting}
                  onChange={(e) => handleInputChange('greeting', e.target.value)}
                  placeholder="Dear {firstName} {lastName},"
                />
                <small>Available variables: {'{firstName}'}, {'{lastName}'}</small>
              </div>

              <div className="form-group">
                <label htmlFor="content">Main Content</label>
                <textarea
                  id="content"
                  rows="4"
                  value={settings.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Your annual submission portal is now available..."
                />
                <small>Available variables: {'{firstName}'}, {'{lastName}'}, {'{year}'}</small>
              </div>

              <div className="form-group">
                <label htmlFor="instructions">Instructions</label>
                <textarea
                  id="instructions"
                  rows="3"
                  value={settings.instructions}
                  onChange={(e) => handleInputChange('instructions', e.target.value)}
                  placeholder="Please complete your submission by the deadline..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="buttonText">Button Text</label>
                <input
                  type="text"
                  id="buttonText"
                  value={settings.buttonText}
                  onChange={(e) => handleInputChange('buttonText', e.target.value)}
                  placeholder="Access Submission Portal"
                />
              </div>

              <div className="form-group">
                <label htmlFor="closing">Closing Message</label>
                <textarea
                  id="closing"
                  rows="3"
                  value={settings.closing}
                  onChange={(e) => handleInputChange('closing', e.target.value)}
                  placeholder="If you have any questions..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="footer">Footer Message</label>
                <textarea
                  id="footer"
                  rows="2"
                  value={settings.footer}
                  onChange={(e) => handleInputChange('footer', e.target.value)}
                  placeholder="This link is unique to you..."
                />
              </div>
            </div>

            <div className="preview-panel">
              <div className="preview-header">
                <h3>Email Preview</h3>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => setPreviewMode(!previewMode)}
                >
                  {previewMode ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>

              {previewMode && (
                <div className="email-preview">
                  <div className="email-preview-container">
                    <div className="email-header">
                      <img src="/images/connectional-logo.svg" alt="Connectional" className="email-logo" />
                      <h1 className="email-title">{settings.title || 'Annual Submission Portal'}</h1>
                      <p className="email-subtitle">{settings.subtitle || 'Tennessee-Western Kentucky Conference'}</p>
                    </div>
                    
                    <div className="email-body">
                      <p>{settings.greeting?.replace('{firstName}', 'John').replace('{lastName}', 'Doe') || 'Dear John Doe,'}</p>
                      
                      <div className="email-content">
                        {settings.content?.replace('{firstName}', 'John').replace('{lastName}', 'Doe').replace('{year}', '2025') || 'Your annual submission portal is now available.'}
                      </div>
                      
                      {settings.instructions && (
                        <div className="email-instructions">
                          <p><strong>Instructions:</strong></p>
                          <p>{settings.instructions}</p>
                        </div>
                      )}
                      
                      <div className="email-button-container">
                        <button className="email-button">
                          {settings.buttonText || 'Access Submission Portal'}
                        </button>
                      </div>
                      
                      <p>{settings.closing || 'If you have any questions, please contact us.'}</p>
                      
                      {settings.footer && (
                        <div className="email-footer-note">
                          <p><small>{settings.footer}</small></p>
                        </div>
                      )}
                      
                      <div className="email-footer">
                        <p>Best regards,<br/>
                        Board of Ordained Ministry<br/>
                        Tennessee-Western Kentucky Conference<br/>
                        The United Methodist Church</p>
                        
                        <p><strong>Support:</strong> support@twkbom.org</p>
                        <p><small>This is an automated message. Please do not reply to this email.</small></p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button 
            className="btn btn-secondary"
            onClick={resetToDefaults}
            type="button"
          >
            Reset to Defaults
          </button>
          <button 
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionLinkEmailTemplateEditor;
